import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Package,
  Search,
  ChevronDown,
  ChevronRight,
  Sparkles,
  RefreshCw,
  Plus,
  Building2,
  PanelLeftClose,
  PanelLeft,
  Boxes,
  Scale,
  ClipboardList,
  ChefHat,
  BarChart3
} from 'lucide-react';

// Import actual inventory components
import StockOverview from './StockOverview';
import InventoryItems from './InventoryItems';
import Recipes from './Recipes';
import UnitsOfMeasurement from './UnitsOfMeasurement';

/**
 * Inventory Hub - Modern Minimal Design with Grouped Menu
 *
 * Clean, minimal design with grouped/collapsible menu sections.
 * Features:
 * - Search bar at top
 * - Grouped menu items (accordion style)
 * - Compact sidebar
 * - Floating action buttons
 * - Modern typography and spacing
 */

const INVENTORY_GROUPS = [
  {
    id: 'stock',
    title: 'Stock Management',
    icon: Boxes,
    items: [
      {
        id: 'overview',
        title: 'Stock Overview',
        icon: BarChart3,
        enabled: true,
        component: StockOverview,
        keywords: ['dashboard', 'low stock', 'alerts', 'summary', 'value'],
      },
      {
        id: 'items',
        title: 'Inventory Items',
        icon: Package,
        enabled: true,
        component: InventoryItems,
        keywords: ['raw materials', 'ingredients', 'stock', 'items', 'vegetables', 'dairy'],
      },
    ],
  },
  {
    id: 'recipes',
    title: 'Recipe Management',
    icon: ChefHat,
    items: [
      {
        id: 'recipes',
        title: 'Recipes',
        icon: ClipboardList,
        enabled: true,
        component: Recipes,
        keywords: ['recipe', 'ingredients', 'menu item', 'link', 'mapping'],
      },
    ],
  },
  {
    id: 'configuration',
    title: 'Configuration',
    icon: Building2,
    items: [
      {
        id: 'uom',
        title: 'Units of Measurement',
        icon: Scale,
        enabled: true,
        component: UnitsOfMeasurement,
        keywords: ['unit', 'kg', 'gram', 'liter', 'piece', 'dozen', 'uom'],
      },
    ],
  },
];

// Flatten items for search
const ALL_ITEMS = INVENTORY_GROUPS.flatMap(g => g.items);

function SearchBar({ value, onChange }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search..."
        className="w-full pl-9 pr-3 py-2 bg-slate-100 border-0 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
      />
    </div>
  );
}

function AccordionSection({ group, expandedGroups, toggleGroup, activeItem, onItemClick, searchQuery, isCollapsed }) {
  const GroupIcon = group.icon;
  const isExpanded = expandedGroups.includes(group.id);
  const hasActiveItem = group.items.some(item => item.id === activeItem);

  // Filter items if searching
  const visibleItems = searchQuery
    ? group.items.filter(item => {
        const query = searchQuery.toLowerCase();
        return item.title.toLowerCase().includes(query) ||
               item.keywords?.some(k => k.includes(query));
      })
    : group.items;

  // Don't show group if no items match search
  if (searchQuery && visibleItems.length === 0) return null;

  // Collapsed mode - show only icons
  if (isCollapsed) {
    return (
      <div className="py-1">
        {visibleItems.map((item) => {
          const ItemIcon = item.icon;
          const isActive = activeItem === item.id;
          return (
            <button
              key={item.id}
              onClick={() => item.enabled && onItemClick(item.id)}
              disabled={!item.enabled}
              title={item.title}
              className={`
                w-full flex items-center justify-center py-2.5 transition-colors relative
                ${isActive ? 'bg-teal-100 text-teal-600' : 'hover:bg-slate-50 text-slate-500'}
                ${!item.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-teal-600 rounded-r-full" />
              )}
              <ItemIcon size={18} />
            </button>
          );
        })}
      </div>
    );
  }

  // Expanded mode - full accordion
  return (
    <div className="border-b border-slate-100 last:border-0">
      {/* Section Header */}
      <button
        onClick={() => toggleGroup(group.id)}
        className={`
          w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
          ${hasActiveItem ? 'bg-teal-50' : 'hover:bg-slate-50'}
        `}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${hasActiveItem ? 'bg-teal-100' : 'bg-slate-100'}`}>
          <GroupIcon size={14} className={hasActiveItem ? 'text-teal-600' : 'text-slate-500'} />
        </div>
        <span className={`flex-1 text-sm font-medium ${hasActiveItem ? 'text-teal-900' : 'text-slate-700'}`}>
          {group.title}
        </span>
        {isExpanded ? (
          <ChevronDown size={16} className="text-slate-400" />
        ) : (
          <ChevronRight size={16} className="text-slate-400" />
        )}
      </button>

      {/* Section Items */}
      {(isExpanded || searchQuery) && (
        <div className="pb-2">
          {visibleItems.map((item) => {
            const ItemIcon = item.icon;
            const isActive = activeItem === item.id;
            return (
              <button
                key={item.id}
                onClick={() => item.enabled && onItemClick(item.id)}
                disabled={!item.enabled}
                className={`
                  w-full flex items-center gap-3 pl-14 pr-4 py-2 text-left transition-colors
                  ${isActive ? 'bg-teal-100 text-teal-900' : 'hover:bg-slate-50 text-slate-600'}
                  ${!item.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <ItemIcon size={14} className={isActive ? 'text-teal-600' : 'text-slate-400'} />
                <span className="flex-1 text-sm">{item.title}</span>
                {!item.enabled && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-500 rounded-full flex items-center gap-0.5">
                    <Sparkles size={8} />
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HeaderActions({ activeItem, componentActions }) {
  if (!componentActions) return null;

  // Stock Overview actions
  if (activeItem === 'overview' && componentActions.onRefresh) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
      </div>
    );
  }

  // Inventory Items actions
  if (activeItem === 'items' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          Add Item
        </button>
      </div>
    );
  }

  // Recipes actions
  if (activeItem === 'recipes' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          Add Recipe
        </button>
      </div>
    );
  }

  // UOM actions
  if (activeItem === 'uom' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          Add Unit
        </button>
      </div>
    );
  }

  return null;
}

function ComingSoonContent() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Sparkles size={28} className="text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Coming Soon</h3>
        <p className="text-sm text-slate-500">This feature is under development.</p>
      </div>
    </div>
  );
}

export default function InventoryHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(['stock', 'recipes', 'configuration']);
  const [componentActions, setComponentActions] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Get active item from URL, default to 'overview'
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ALL_ITEMS.map(item => item.id);
  const activeItem = validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview';

  // Handle item click - update URL instead of local state
  const handleItemClick = (itemId) => {
    setSearchParams({ tab: itemId }, { replace: true });
  };

  // Ensure the group containing the active item is expanded
  useEffect(() => {
    const activeGroup = INVENTORY_GROUPS.find(g =>
      g.items.some(item => item.id === activeItem)
    );
    if (activeGroup && !expandedGroups.includes(activeGroup.id)) {
      setExpandedGroups(prev => [...prev, activeGroup.id]);
    }
  }, [activeItem]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  // Find active item and component
  const activeMenuItem = ALL_ITEMS.find(item => item.id === activeItem);
  const ActiveComponent = activeMenuItem?.component;

  const handleActionsReady = (actions) => {
    setComponentActions(actions);
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Sidebar Menu (Collapsible) */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-slate-200 flex flex-col transition-all duration-300`}>
        {/* Header */}
        <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-b border-slate-200`}>
          {sidebarCollapsed ? (
            // Collapsed Header - Just icon and toggle
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <Package size={16} className="text-white" />
              </div>
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                title="Expand Sidebar"
              >
                <PanelLeft size={18} />
              </button>
            </div>
          ) : (
            // Expanded Header
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                  <Package size={16} className="text-white" />
                </div>
                <h1 className="text-lg font-bold text-slate-900 flex-1">Inventory</h1>
                <button
                  onClick={toggleSidebar}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                  title="Collapse Sidebar"
                >
                  <PanelLeftClose size={18} />
                </button>
              </div>
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
            </>
          )}
        </div>

        {/* Accordion Sections */}
        <div className="flex-1 overflow-y-auto">
          {INVENTORY_GROUPS.map((group) => (
            <AccordionSection
              key={group.id}
              group={group}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
              activeItem={activeItem}
              onItemClick={handleItemClick}
              searchQuery={searchQuery}
              isCollapsed={sidebarCollapsed}
            />
          ))}
        </div>

        {/* Footer */}

      </div>

      {/* Right Panel - Content */}
      <div className="flex-1 bg-slate-50 overflow-hidden flex flex-col">
        {/* Content Body - No separate header, title shown in sidebar */}
        <div className="flex-1 overflow-y-auto">
          {activeMenuItem?.enabled && ActiveComponent ? (
            <ActiveComponent
              renderActions={handleActionsReady}
              title={activeMenuItem?.title}
              actions={<HeaderActions activeItem={activeItem} componentActions={componentActions} />}
            />
          ) : (
            <ComingSoonContent />
          )}
        </div>
      </div>
    </div>
  );
}